/**
 * BrokerRowPlace — match a held broker row to a security, or put it on a deal
 * ===========================================================================
 * Brian, 27 October 2026, on Approvals: "why cant i interact with these things
 * here and it only says rule out?" (D-284). A row the importer could not match
 * to a security on the book, or to a deal, offered only Rule out; the answer it
 * needed was a pipeline action. This is that answer, on the row:
 *
 *   - Match security…  search the book's securities, pick one, and the server
 *     says which deal that puts it on, or which deals to choose from;
 *   - Choose deal…     the deals that trade in the row's security (any deal,
 *     for a cash row).
 *
 * Each choice is a dry run first, in the server's words, then the button that
 * commits (CLAUDE.md §1). A placed row is swept like any other and posts when
 * it is clean.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { securitiesPath } from "./links";
import { BLOTTER_REVIEW_URL } from "./needsReview";

const WORDS = {
  security: { open: "Match security…", commit: "Match it" },
  deal: { open: "Choose deal…", commit: "Put it on this deal" },
};

/**
 * What to search the book for, from what the broker printed: the name as
 * given, then without a warrant or unit marker ("WT-", " TBA") and a legal
 * form, then its first two words. A broker's "WT-UNIVERSAL DIGITAL INC TBA"
 * matches no security; "UNIVERSAL DIGITAL" finds the LIFE warrants.
 */
export function searchTerms(text) {
  const given = String(text || "").trim();
  if (!given) return [];
  const bare = given
    .replace(/^(WTS?|WT)[\s-]+/i, "")
    .replace(/\s+(TBA|UNITS?|WTS?|RTS?)$/i, "")
    .replace(/[,.]/g, " ")
    .replace(/\s+(INC|CORP|CORPORATION|LTD|LIMITED|PLC|AB|LLC|CO)\b.*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  const words = bare.split(" ").filter(Boolean);
  const terms = [given, bare, words.slice(0, 2).join(" ")];
  return terms.filter((t, i) => t && t.length >= 2 && terms.indexOf(t) === i);
}

const listOf = (data) => (Array.isArray(data) ? data : data?.results || []);
const securityLabel = (s) => [s.name || s.ticker, s.ticker && s.name ? s.ticker : null]
  .filter(Boolean).join(" · ");

export default function BrokerRowPlace({ item, onDone, onCancel }) {
  const needs = item.needs;
  const words = WORDS[needs];
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [security, setSecurity] = useState(null);
  const [dealId, setDealId] = useState("");
  const [why, setWhy] = useState("");
  const [preview, setPreview] = useState(null);
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (terms) => {
    setError(null);
    for (const term of terms) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await api.get("/securities/securities/", { params: { search: term, page_size: 12 } });
        const found = listOf(res.data);
        if (found.length || term === terms[terms.length - 1]) {
          setQuery(term);
          setResults(found);
          return;
        }
      } catch (e) {
        setError("The securities search did not answer. Try again.");
        return;
      }
    }
  }, []);

  const check = useCallback(async ({ securityId, deal, commit = false }) => {
    setBusy(true);
    setError(null);
    const body = { why, commit };
    if (securityId) body.security_id = securityId;
    if (deal) body.deal_id = deal;
    try {
      const res = await api.post(BLOTTER_REVIEW_URL(item.row_id, "place"), body);
      if (commit) onDone(item.key, res.data.reads_as || "Placed.");
      else {
        setPreview(res.data);
        setDeals(res.data.candidate_deals || []);
      }
    } catch (e) {
      setPreview(null);
      setError(e?.response?.data?.detail || "The server did not answer. Try again.");
    } finally {
      setBusy(false);
    }
  }, [why, item.row_id, item.key, onDone]);

  useEffect(() => {
    // A row that needs a security starts on a search for the broker's own
    // name; a row that needs a deal starts on the deals it could go on.
    if (needs === "security") search(searchTerms(item.search || item.company));
    else check({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = (s) => {
    setSecurity(s);
    setDealId("");
    setDeals([]);
    setPreview(null);
    check({ securityId: s.id });
  };
  const pickDeal = (value) => {
    setDealId(value);
    setPreview(null);
    if (value) check({ securityId: security?.id, deal: value });
  };

  // One deal trading in the security is chosen by the server; several, or a
  // row that needs a deal, are a choice for the reader.
  const showDeals = deals.length > 1 || (needs === "deal" && deals.length > 0);
  const ready = Boolean(preview && why.trim()
    && (needs === "security" ? security && (!showDeals || dealId) : dealId));

  return (
    <form className="nr-decision nr-place" onSubmit={(e) => { e.preventDefault(); search(searchTerms(query)); }}>
      {needs === "security" && (
        <>
          <input aria-label="Search the book's securities" value={query}
                 onChange={(e) => setQuery(e.target.value)} placeholder="Company, ticker or CUSIP" />
          <button type="submit" disabled={busy || !query.trim()}>Search</button>
          {results && (
            <ul className="nr-results" aria-label="Securities on the book">
              {results.length === 0 && (
                <li className="nr-none">
                  Nothing on the book matches “{query}”.{" "}
                  <Link to={securitiesPath(query)}>Add the security</Link>, then match it here.
                </li>
              )}
              {results.map((s) => (
                <li key={s.id}>
                  <button type="button" aria-pressed={security?.id === s.id} onClick={() => choose(s)}>
                    {securityLabel(s)}
                  </button>
                  {s.instrument_type ? <span className="nr-kind">{s.instrument_type}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {showDeals && (
        <select aria-label="Which deal" value={dealId} onChange={(e) => pickDeal(e.target.value)}>
          <option value="">Choose the deal…</option>
          {deals.map((d) => (
            <option key={d.deal_id} value={d.deal_id}>
              {d.company && d.company !== d.name ? `${d.company} — ${d.name}` : d.name}
            </option>
          ))}
        </select>
      )}
      {preview?.reads_as && <p className="nr-preview" role="status">{preview.reads_as}</p>}
      {preview && (
        <>
          <input aria-label={`${words.commit}: why`} placeholder="Why, e.g. the ISIN on the broker's row matches it"
                 value={why} onChange={(e) => setWhy(e.target.value)} />
          <button type="button" className="nr-commit" disabled={busy || !ready}
                  onClick={() => check({ securityId: security?.id, deal: dealId, commit: true })}>
            {words.commit}
          </button>
        </>
      )}
      <button type="button" className="nr-cancel" onClick={onCancel}>Cancel</button>
      {error && <p className="nr-error" role="alert">{error}</p>}
    </form>
  );
}

export { WORDS as PLACE_WORDS };
